import * as React from "react"
import { Search, X } from "lucide-react"

import { cn } from "../lib/utils"
import { Input } from "./input"

interface SearchInputProps
  extends Omit<React.ComponentProps<"input">, "onChange" | "value" | "type"> {
  /** The current query. */
  value: string
  /** Called with the new query text (not the event). */
  onChange: (value: string) => void
  /**
   * Accessible name for the field. Visually hidden — the magnifier and the
   * placeholder carry the meaning on screen.
   */
  label?: string
  /** Class names for the wrapper rather than the `<input>`. */
  containerClassName?: string
}

/**
 * A search field: magnifier, placeholder and a clear button that appears once
 * there is something to clear. Used by {@link DataTable}'s toolbar, and by
 * hand-built grids that filter with `matchesSearch`.
 */
const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      value,
      onChange,
      label = "Search",
      placeholder = "Search...",
      className,
      containerClassName,
      ...props
    },
    ref
  ) => {
    // Clearing unmounts the clear button, so focus would otherwise fall to
    // <body> and a keyboard user would lose the field. Hand it back.
    const inputRef = React.useRef<HTMLInputElement>(null)
    React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement)

    return (
      <div className={cn("relative", containerClassName)}>
        <Search
          aria-hidden="true"
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400"
        />
        <Input
          ref={inputRef}
          type="search"
          aria-label={label}
          placeholder={placeholder}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          // The UA's own clear affordance would sit on top of ours.
          className={cn(
            "pl-9 pr-9 [&::-webkit-search-cancel-button]:appearance-none",
            className
          )}
          {...props}
        />
        {value !== "" && (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => {
              onChange("")
              inputRef.current?.focus()
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-1 text-neutral-400 transition-colors hover:text-neutral-600 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            <X aria-hidden="true" className="h-4 w-4" />
          </button>
        )}
      </div>
    )
  }
)
SearchInput.displayName = "SearchInput"

export { SearchInput }
export type { SearchInputProps }
